import React, { useCallback, useMemo, useRef } from "react";
import { Combobox, type ComboboxItem } from "@bloomberg/media.atomics";
import type { TickerItem } from "./types";
import styles from "./styles.module.css";

const ALL_TICKERS_VALUE = "ALL";

interface TickersFilterProps {
  readonly tickers: readonly TickerItem[];
  readonly allSelected: boolean;
  readonly selectedTickers: Set<string>;
  readonly onToggleTicker: (ticker: string) => void;
}

export function TickersFilter({
  tickers,
  allSelected,
  selectedTickers,
  onToggleTicker,
}: TickersFilterProps) {
  const items: ComboboxItem[] = useMemo(
    () => [
      { value: ALL_TICKERS_VALUE, label: "All Tickers" },
      ...tickers.map((t) => ({ value: t.ticker, label: t.companyName })),
    ],
    [tickers]
  );

  const itemsByValue = useMemo(() => {
    const map = new Map<string, string>();

    for (const item of items) {
      map.set(item.value, item.label);
    }

    return map;
  }, [items]);

  const filterByLabel = useCallback(
    (itemValue: unknown, query: string) => {
      const label = itemsByValue.get(itemValue as string) ?? "";

      return label.toLowerCase().includes(query.toLowerCase());
    },
    [itemsByValue]
  );

  const value = useMemo(() => {
    if (allSelected) {
      return [ALL_TICKERS_VALUE];
    }

    return Array.from(selectedTickers);
  }, [allSelected, selectedTickers]);

  // Translates the Combobox's array-based value changes into individual
  // toggle calls expected by useTickersFilter. Handles 4 scenarios:
  const handleValueChange = useCallback(
    (newValue: string[]) => {
      const specificTickers = newValue.filter((v) => v !== ALL_TICKERS_VALUE);
      const hasAll = newValue.includes(ALL_TICKERS_VALUE);

      // 1. User clicked a specific ticker while "All" is active — toggle that ticker on
      if (allSelected && specificTickers.length > 0) {
        onToggleTicker(specificTickers[0]);

        return;
      }

      // 2. User unchecked "All" directly — no-op (must select a specific ticker instead)
      if (allSelected && !hasAll) {
        return;
      }

      // 3. User clicked "All" while specific tickers are selected — clear all selections
      if (!allSelected && hasAll) {
        for (const ticker of selectedTickers) {
          onToggleTicker(ticker);
        }

        return;
      }

      // 4. Normal add/remove of an individual ticker
      const added = specificTickers.find((t) => !selectedTickers.has(t));
      const removed = Array.from(selectedTickers).find(
        (t) => !specificTickers.includes(t)
      );
      const changed = added ?? removed;

      if (changed) {
        onToggleTicker(changed);
      }
    },
    [allSelected, selectedTickers, onToggleTicker]
  );

  const portalRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <Combobox
        labelText="Filter by ticker"
        items={items}
        multiple
        value={value}
        onValueChange={handleValueChange}
        filter={filterByLabel}
        portalTarget={portalRef}
      />
      <div ref={portalRef} className={styles.tickersDropdown} />
    </>
  );
}
